import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import {
  AmpelBadgeComponent,
  ampelFor,
  AmpelState,
  countOpenDefects,
  openDefectKeys,
} from '../../../components/ampel-badge/ampel-badge.component';
import { StackBarComponent } from '../../../components/stack-bar/stack-bar.component';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { ReleasePlanResult, ReleaseResultsFilters } from '../../../models/release-results.model';
import { passRateTone, passtZurSuche, releaseResultsFilters } from '../test-results.utils';
import { ReleaseListState } from '../test-results.types';
import { PlanFaelleTabelleComponent } from './plan-faelle-tabelle/plan-faelle-tabelle.component';

interface PlansState {
  loading: boolean;
  error: boolean;
  data: ReleasePlanResult[] | null;
}

interface PlansRequest {
  projectId: string;
  releaseId: string | null;
  filters: ReleaseResultsFilters;
  aktualisierung: number;
  versuch: number;
}

interface PlanRow {
  plan: ReleasePlanResult;
  ausgefuehrt: number;
  passRate: number | null;
  fehler: number;
  ampel: AmpelState;
  offeneFehler: string[];
}

/**
 * Sicht „Testpläne" (page-release-freigabe.jsx:452-524): je Plan des Releases Fortschritt,
 * Pass-Rate, Fehler und Ampel; aufgeklappt die Testfälle mit ihrem letzten Ergebnis.
 * Lädt mit denselben Filtern wie die Freigabe-Karte, damit die Summen übereinstimmen.
 */
@Component({
  selector: 'app-plan-ergebnis-tabelle',
  standalone: true,
  imports: [TranslatePipe, MatIcon, RouterLink, AmpelBadgeComponent, StackBarComponent, PlanFaelleTabelleComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './plan-ergebnis-tabelle.component.html',
  styleUrl: './plan-ergebnis-tabelle.component.scss',
})
export class PlanErgebnisTabelleComponent {
  private readonly resultsService = inject(ReleaseResultsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly releaseId = input<string | null>(null);
  readonly releasesState = input<ReleaseListState>('loaded');
  readonly environment = input<string | null>(null);
  /** Vorgewählter Plan, etwa beim Einstieg aus dem Testplan: Seine Zeile ist nach dem Laden aufgeklappt. */
  readonly planKey = input<string | null>(null);
  readonly suche = input('');
  /** Wechselt nach einem Import; die Daten werden dann neu geholt. */
  readonly aktualisierung = input(0);

  /** Plan-Key für die Sicht „Testläufe" mit Plan-Filter. */
  readonly laeufeOeffnen = output<string | null>();
  readonly releasesNeuLaden = output<void>();

  private readonly versuch = signal(0);

  protected readonly state = signal<PlansState>({ loading: false, error: false, data: null });
  private readonly expanded = signal<ReadonlySet<string>>(new Set());

  protected readonly keinPlanImRelease = computed(() => !this.state().data?.length);

  protected readonly rows = computed<PlanRow[]>(() =>
    (this.state().data ?? [])
      .filter((plan) =>
        passtZurSuche(this.suche(), [
          plan.key,
          plan.titel,
          plan.verantwortlich,
          ...plan.faelle.flatMap((fall) => [fall.testCaseKey, fall.titel]),
        ]),
      )
      .map((plan) => {
        const ausgefuehrt = plan.gesamt - plan.offen;
        return {
          plan,
          ausgefuehrt,
          passRate: ausgefuehrt ? Math.round((plan.passed / ausgefuehrt) * 100) : null,
          fehler: plan.failed + plan.blocked,
          ampel: ampelFor(plan, countOpenDefects(plan.faelle)),
          offeneFehler: openDefectKeys(plan.faelle),
        };
      }),
  );

  private readonly request = computed<PlansRequest>(() => {
    return {
      projectId: this.projectId(),
      releaseId: this.releaseId(),
      filters: releaseResultsFilters(this.environment()),
      aktualisierung: this.aktualisierung(),
      versuch: this.versuch(),
    };
  });

  protected erneutLaden(): void {
    this.versuch.update((v) => v + 1);
  }

  constructor() {
    toObservable(this.request)
      .pipe(
        switchMap((req) => {
          if (!req.releaseId) return of<PlansState>({ loading: false, error: false, data: null });
          return this.resultsService.getPlans(req.projectId, req.releaseId, req.filters).pipe(
            map((data): PlansState => ({ loading: false, error: false, data })),
            catchError(() => of<PlansState>({ loading: false, error: true, data: null })),
            startWith<PlansState>({ loading: true, error: false, data: null }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        this.state.set(state);
        const preselected = state.data?.find((plan) => plan.key !== null && plan.key === this.planKey());
        if (preselected) this.expanded.set(new Set([preselected.id]));
      });
  }

  protected isOpen(planId: string): boolean {
    return this.expanded().has(planId);
  }

  protected toggle(planId: string): void {
    const next = new Set(this.expanded());
    if (!next.delete(planId)) next.add(planId);
    this.expanded.set(next);
  }

  protected onRowKeydown(event: KeyboardEvent, planId: string): void {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.toggle(planId);
  }

  protected readonly passRateTone = passRateTone;
}
